import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DollarSign, Users, Building2, CreditCard, Plus, Send, CheckCircle, XCircle, Clock, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function BillingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [config, setConfig] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [userCount, setUserCount] = useState(1);
  const [setupFeeAmount, setSetupFeeAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  // Handle return from Stripe
  useEffect(() => {
    const subSuccess = searchParams.get('subscription');
    const setupSuccess = searchParams.get('setup_fee');
    const sessionId = searchParams.get('session_id');

    if (subSuccess === 'success' && sessionId) {
      organizationsAPI.activateSubscription(sessionId)
        .then(() => { toast.success('Subscription activated!'); loadData(); })
        .catch(() => toast.error('Failed to activate subscription'));
    } else if (subSuccess === 'cancelled') {
      toast.info('Subscription checkout cancelled');
    }

    if (setupSuccess === 'success' && sessionId) {
      organizationsAPI.getSetupFeeStatus(sessionId)
        .then(() => { toast.success('Setup fee payment confirmed!'); loadData(); })
        .catch(() => toast.info('Setup fee payment processing...'));
    } else if (setupSuccess === 'cancelled') {
      toast.info('Setup fee payment cancelled');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [overviewRes, configRes, orgsRes] = await Promise.all([
        organizationsAPI.getBillingOverview(),
        organizationsAPI.getSubscriptionConfig(),
        organizationsAPI.getAll(),
      ]);
      setOverview(overviewRes.data);
      setConfig(configRes.data);
      setOrgs(orgsRes.data || []);
    } catch (e) { toast.error('Failed to load billing data'); }
    setLoading(false);
  };

  const handleCreateSubscription = async () => {
    if (!selectedOrg) return toast.error('Select an organization');
    setActionLoading(true);
    try {
      const res = await organizationsAPI.createSubscription(
        { organization_id: selectedOrg, user_count: parseInt(userCount) },
        window.location.origin
      );
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create subscription');
    }
    setActionLoading(false);
  };

  const handleChargeSetupFee = async () => {
    if (!selectedOrg) return toast.error('Select an organization');
    setActionLoading(true);
    try {
      const amount = setupFeeAmount ? parseFloat(setupFeeAmount) : undefined;
      const res = await organizationsAPI.chargeSetupFee(
        { organization_id: selectedOrg, amount },
        window.location.origin
      );
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create setup fee charge');
    }
    setActionLoading(false);
  };

  const handleCancelSubscription = async (orgId) => {
    if (!confirm('Cancel this subscription? It will remain active until the end of the billing period.')) return;
    try {
      await organizationsAPI.cancelSubscription(orgId);
      toast.success('Subscription will cancel at period end');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      past_due: 'bg-amber-100 text-amber-700',
      pending: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700',
      locked: 'bg-red-100 text-red-700',
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-600'}>{status || 'none'}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  // Orgs without active subscription for the dropdown
  const unsubscribedOrgs = orgs.filter(o => !o.subscription_status || o.subscription_status === 'none' || o.subscription_status === 'cancelled' || o.subscription_status === 'expired');

  return (
    <div className="flex h-screen bg-background" data-testid="billing-page">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold" data-testid="billing-title">Billing & Subscriptions</h1>
              <p className="text-sm text-muted-foreground">Manage organization subscriptions and setup fees</p>
            </div>
            <div className="flex gap-2">
              <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="charge-setup-fee-btn">
                    <Send className="w-4 h-4 mr-2" />Charge Setup Fee
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Charge One-Time Setup Fee</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">Bill an organization for their initial platform setup.</p>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Organization</Label>
                      <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                        <SelectTrigger data-testid="setup-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                        <SelectContent>
                          {orgs.map(o => (
                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount (leave blank for default ${config?.default_setup_fee})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={`${config?.default_setup_fee || 500}`}
                        value={setupFeeAmount}
                        onChange={(e) => setSetupFeeAmount(e.target.value)}
                        data-testid="setup-fee-amount"
                      />
                    </div>
                    <Button onClick={handleChargeSetupFee} disabled={actionLoading} className="w-full" data-testid="setup-fee-submit">
                      {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      Charge Setup Fee
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-subscription-btn">
                    <Plus className="w-4 h-4 mr-2" />New Subscription
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Subscription</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">Set up monthly recurring billing for an organization.</p>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Organization</Label>
                      <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                        <SelectTrigger data-testid="sub-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                        <SelectContent>
                          {unsubscribedOrgs.map(o => (
                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Number of Users</Label>
                      <Input
                        type="number"
                        min="1"
                        value={userCount}
                        onChange={(e) => setUserCount(e.target.value)}
                        data-testid="sub-user-count"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        ${config?.price_per_user}/user/month = <strong>${(config?.price_per_user || 49) * (parseInt(userCount) || 1)}/month</strong>
                      </p>
                    </div>
                    <Button onClick={handleCreateSubscription} disabled={actionLoading} className="w-full" data-testid="sub-submit">
                      {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      Create Subscription (${(config?.price_per_user || 49) * (parseInt(userCount) || 1)}/mo)
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" data-testid="mrr-value">${overview?.total_mrr?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">Monthly Recurring Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{overview?.active_subscriptions || 0}</p>
                    <p className="text-xs text-muted-foreground">Active Subscriptions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{overview?.total_organizations || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Organizations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${config?.price_per_user || 49}</p>
                    <p className="text-xs text-muted-foreground">Per User / Month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Organization Subscriptions Table */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Organization Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Organization</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Subscription</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Users</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Monthly</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Setup Fee</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Stripe Connect</th>
                      <th className="text-right py-3 px-2 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview?.organizations || []).map(org => {
                      const sub = (overview?.subscriptions || []).find(s => s.organization_id === org.id);
                      return (
                        <tr key={org.id} className="border-b last:border-0" data-testid={`billing-org-${org.id}`}>
                          <td className="py-3 px-2 font-medium">{org.name}</td>
                          <td className="py-3 px-2">{statusBadge(sub?.status || org.subscription_status)}</td>
                          <td className="py-3 px-2">{sub?.user_count || '-'}</td>
                          <td className="py-3 px-2">{sub ? `$${sub.monthly_amount}` : '-'}</td>
                          <td className="py-3 px-2">
                            {org.setup_fee_paid ?
                              <Badge className="bg-green-100 text-green-700">Paid</Badge> :
                              <Badge className="bg-gray-100 text-gray-600">Unpaid</Badge>
                            }
                          </td>
                          <td className="py-3 px-2">{statusBadge(org.stripe_connect_status)}</td>
                          <td className="py-3 px-2 text-right">
                            {sub?.status === 'active' && (
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleCancelSubscription(org.id)} data-testid={`cancel-sub-${org.id}`}>
                                <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Date</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Type</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Amount</th>
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview?.recent_transactions || []).map((txn, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="py-3 px-2">{new Date(txn.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-2 capitalize">{txn.type || 'invoice'}</td>
                        <td className="py-3 px-2 font-medium">${txn.amount?.toFixed(2)}</td>
                        <td className="py-3 px-2">{statusBadge(txn.status)}</td>
                      </tr>
                    ))}
                    {(!overview?.recent_transactions || overview.recent_transactions.length === 0) && (
                      <tr><td colSpan="4" className="py-6 text-center text-muted-foreground">No transactions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
